/* eslint-disable no-console */

/* ══════════════════════════════════════════════════════════════════════════════
 *  ⚠️  THIS FILE IS A TEMPLATE. THE COPY IN THE CONSUMING REPO IS GENERATED.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 *  You are reading the SOURCE. It is copied verbatim into every consuming repo as
 *  `src/packages/shared/teselly-client-library/src/base/SSE.ts` (and the equivalent
 *  path in any other consumer) every time `pv3 g cl` / `yarn generate-all` runs.
 *
 *  IF YOU ARRIVED HERE FROM THE GENERATED COPY: edit THIS file, not that one.
 *
 *  IF YOU ARE ABOUT TO EDIT THE GENERATED COPY: don't. Your change will be
 *  silently reverted by the next generation run, and — this is the dangerous part
 *  — the consuming repo's drift check DOES NOT COVER `src/base/*`, so nothing will
 *  tell you it happened. The build stays green, the tests stay green, and the
 *  behaviour reverts.
 *
 *  This is not hypothetical. TV1-1535 slice 8 removed the authorization headers
 *  from `resolveAuthHeaders` below in the teselly repo. The very first
 *  `yarn generate-all` afterwards restored all five of them, re-opening a header
 *  the backend had just stopped trusting, with no diff in any watched path. That
 *  edit is now made HERE, which is why it survives.
 *
 *  See also: bett3r-dev/pv3#66.
 * ══════════════════════════════════════════════════════════════════════════════ */

import rison from '@bett3r-dev/rison';

/**
 * Marker-sniff encoder. Companion / contract-of-record for the server-side
 * `decodeObjectQueryParams`. Objects and arrays are rison-encoded; all other
 * defined values are stringified via `String( value )`. `undefined` and `null`
 * values are skipped entirely.
 *
 * No schema is required: rison output always begins with `(` (objects) or
 * `!(` (arrays), markers that primitive strings cannot produce unescaped.
 * The server-side decoder detects these markers to know when to rison-decode.
 */
const encodeObjectQueryParams = ( query: Record<string, unknown> ): Record<string, string> => {
  const result: Record<string, string> = {};
  if ( !query || typeof query !== 'object' ) return result;

  for ( const [ key, value ] of Object.entries( query )) {
    if ( value === undefined || value === null ) continue;
    if ( Array.isArray( value ) || typeof value === 'object' ) {
      result[key] = rison.encode_uri( value );
    } else {
      result[key] = String( value );
    }
  }

  return result;
};

/** OneOf type helpers */
export type Without<T, U> = {[P in Exclude<keyof T, keyof U>]?: never };
export type XOR<T, U> = ( T | U ) extends object ? ( Without<T, U> & U ) | ( Without<U, T> & T ) : T | U;
export type OneOf<T extends any[]> = T extends [infer Only] ? Only : T extends [infer A, infer B, ...infer Rest] ? OneOf<[XOR<A, B>, ...Rest]> : never;
export type LooseAutocomplete<T extends string> = T | ( string & {});
/**
 * The identity an in-process hop inherits from its caller.
 *
 * This carries IDENTITY, not AUTHORIZATION. `permissionScopes` used to relay a flat
 * per-request scope array — the scopes the gateway computed for the route the caller
 * ENTERED through, with no record of which resource they described. Relaying it made a
 * hop enforce one resource's grant against another, so it is gone.
 *
 * Do not add an authorization tier back here. A hop serializes this type into
 * `x-auth-*` headers on a synthetic request, and a header is forgeable; authorization
 * belongs in the ambient `ExecutionContext.userAttributes`, which no transport can
 * populate from outside the process.
 */
export type ExecutionContextProvider = () => { accountId?: string; userId?: string; correlationId?: string; causationId?: string; userType?: string; roles?: string[]; permissions?: string[]; assignedScopes?: string[]; capabilities?: string[]; impersonatedBy?: string } | undefined;

export type BackendConfig = {
  baseUrl: string,
  includeCredentials?: boolean,
  token?: string
};

export type EventMetadata = {
  causationId?: string | undefined;
  correlationId?: string | undefined;
  //FIXME: This is a hack to allow the metadata to be any
  userId?: string,
  accountId?: string
  [key: string]: any
};

export type CommittedEvent<Payload = any> = {
  name: string,
  data?: Payload,
  stream: string
  metadata: EventMetadata
  id: string
  position: number
  timestamp: string
  version: number
};

export type SystemCommandParams<Body=any> = {
  query?: {
    expectedVersion?: string | null
    correlationId?: string
    causationId?: string,
    [key: string]: any
  },
  body?: Body
};

export type AggregateCommandParams<Body=any> = SystemCommandParams<Body> & {
  id: string
};

export type CommandExecutionResponse<Model = any, Event = any> = {
  state?: Model,
  events: CommittedEvent<Event>[]
};

export type ReadmodelChange<T = any> =
  | { type: 'insert', data: T }
  | { type: 'update', data: T, previous: T }
  | { type: 'delete', previous: T }
  | { type: 'snapshot', data: { items: T[], count: number }}
  | { type: 'signal' };

export type SubscriptionOptions = {
  _snapshot?: boolean;
  _changes?: 'delta' | 'signal';
};

export type FetchResponse<T = any> = Response & {
  bodyParsed: T
};

export type Document = {};

export type CollectionFilterOR<T extends Document = any> = {
  OR: QueryFilter<T>[]
};

export type CollectionFilterAND<T extends Document = any> = {
  AND: QueryFilter<T>[]
};

export type DatabaseOps =
  | 'eq'
  | 'neq'
  | 'lt'
  | 'gt'
  | 'lte'
  | 'gte'
  | 'in'
  | 'nin'
  | 'contains'
  | 'notContains'
  | 'some'
  | 'none'
  | 'every'
  | 'exists'
  | 'notExists';

export type CollectionFilterInstance<Model extends Document> = {
  field: LooseAutocomplete<keyof Model & string>;
  op: LooseAutocomplete<DatabaseOps>;
  value: any;
};

export type QueryFilter<T extends Document = any> = Partial<
& CollectionFilterOR<T>
& CollectionFilterAND<T>
& CollectionFilterInstance<T>
>;

export type QuerySort<Model extends Document = any> = {
  field: keyof Model;
  direction?: 'asc' | 'desc';
};

export type KeyWithBang<T> = {
  [K in keyof T]: K & string | `!${K & string}` | `DISTINCT:${K & string}`
}[keyof T];

export type ReadmodelQueryBase<Model extends Document = any> = {
  limit?: number;
  offset?: number;
  after?: string | number;
  sort?: QuerySort<Model>[];
  filter?: QueryFilter<Model>[];
  select?: KeyWithBang<Model> | KeyWithBang<Model>[];
  byDistinctField?: LooseAutocomplete<keyof Model & string>;
};

export const parseResponseBody = ( type:'json' | 'text' ,response: Response, method: 'reject' | 'resolve' ): Promise<void | FetchResponse> =>{
  if ( response.status === 204 ) return Promise[method]( Object.assign( response, {
    bodyParsed: undefined
  }) as FetchResponse );
  return response[type]()
    .then(( bodyParsed ) => {
      return Promise[method]( Object.assign( response, {
        bodyParsed
      }) as FetchResponse );
    });
};
export const processFetchResponse = <T = any>( response: Response ): Promise<void | FetchResponse<T>> =>{
  if ( response.ok === true )
    return parseResponseBody( 'json', response, 'resolve' );
  else
    return parseResponseBody( 'json', response, 'reject' );
};

export const parseQueryParams = ( path: string, query?: Record<string, any> ) =>{
  const pathParams = ( path.match( /:([a-zA-Z0-9_]+)\??/g ) || []).map( p => p.replace( /(:|\?)/g, '' ));//?
  if ( !query || !Object.keys( query ).length ) return '';

  const filteredEntries = Object.entries( query ).filter(([ k, v ]) => !pathParams.includes( k ) && v !== undefined );
  if ( !filteredEntries.length ) return '';

  // Marker-sniff encoding: objects/arrays become rison, primitives stringify,
  // null/undefined are skipped. No schema required — the server-side decoder
  // detects the `(` / `!(` rison markers to know when to decode.
  const filteredQuery = Object.fromEntries( filteredEntries );
  const encoded = encodeObjectQueryParams( filteredQuery );
  const result = new URLSearchParams();
  for ( const [ key, value ] of Object.entries( encoded )) {
    result.append( key, value );
  }

  const resultString = result.toString();
  return resultString && resultString.length > 0 ? `?${resultString}` : '';
};

export const formatRequestParams = ( path: string, params: Record<string, any> = {}) => {
  return path
    .replace( /:([a-zA-Z0-9_]+)\?/g, ( _, key ) => params[key]?.toString() ?? '' ) // optional
    .replace( /:([a-zA-Z0-9_]+)/g, ( _, key ) => {
      if ([ undefined, '' ].includes( params[key]?.toString() ?? '' )) throw new Error( `Missing required param: ${key}` );
      return params[key]!;
    });
};

export const filterUndefinedHeaders = ( headers: Record<string, string | undefined> ) => {
  return Object.fromEntries( Object.entries( headers ).filter(([ , value ]) => value !== undefined )) as Record<string, string>;
};

export const buildRequestBody = ( body: any, isMultipart: boolean = false ): { body: any; headers: Record<string, string> } => {
  if ( !body ) {
    return { body: undefined, headers: {}};
  }

  if ( isMultipart ) {
    const formData = new FormData();
    
    // Handle FormData construction
    Object.entries( body ).forEach(([ key, value ]) => {
      if ( value !== undefined && value !== null ) {
        if ( value instanceof File || value instanceof Blob ) {
          formData.append( key, value );
        } else if ( typeof value === 'object' ) {
          // For complex objects, stringify them
          formData.append( key, JSON.stringify( value ));
        } else {
          formData.append( key, String( value ));
        }
      }
    });

    // Don't set Content-Type for FormData - browser will set it with boundary
    return { body: formData, headers: {}};
  } else {
    // JSON request
    return { 
      body: JSON.stringify( body ), 
      headers: { 'Content-Type': 'application/json' }
    };
  }
};

export type Requests = ReturnType<typeof requests>;
export const requests = ( config: BackendConfig, _fetch = fetch, headers: Record<string, string | undefined> = {}, contextProvider: ExecutionContextProvider ) => {
  const defaultHeaders: Record<string, any> = {
    ...headers
  };
  if ( config.token ){
    defaultHeaders['Authorization'] = `Bearer ${config.token}`;
  }

  const resolveContext = () => contextProvider?.();

  const mergeContextMetadata = ( params: Record<string, any>, ctx: ReturnType<typeof resolveContext> ): Record<string, any> => {
    if ( !ctx ) return params;
    return {
      ...( ctx.correlationId && !params.correlationId ? { correlationId: ctx.correlationId } : {}),
      ...( ctx.causationId && !params.causationId ? { causationId: ctx.causationId } : {}),
      ...params
    };
  };

  const resolveAuthHeaders = ( baseHeaders: Record<string, any>, explicitHeaders: Record<string, string | undefined>, ctx: ReturnType<typeof resolveContext> ): Record<string, any> => {
    const hasExplicitAuth = explicitHeaders?.['x-auth-account-id'] || explicitHeaders?.['x-auth-user-id'];
    if ( hasExplicitAuth || !ctx ) return baseHeaders;
    // IDENTITY ONLY. The authorization tiers — `userType`, `roles`, `permissions`,
    // `assignedScopes`, `capabilities` — are deliberately NOT serialized onto a hop's
    // headers, and a consuming backend must not read them off any header. Authority
    // belongs on a signed credential or an ambient in-process context, neither of which
    // an outside caller can forge. A header can be set by anyone who can reach the port.
    //
    // Do not "restore" these for convenience. Re-adding `x-auth-user-type` alone is enough
    // to hand any caller an admin bypass in a backend that trusts it (teselly TV1-1535
    // slice 8: it drove the tenant filter to return completely unfiltered).
    //
    // This also retires a latent defect rather than fixing it in place: `x-auth-roles` was
    // assigned a `string[]` with NO `JSON.stringify`. In-process that survived because the
    // reader checked `Array.isArray` first — over a real HTTP hop the array comma-joins and
    // parses back to `[]`, a silently empty authorization tier. There is no array-valued
    // auth header left to get this wrong. Do not add one back.
    return {
      ...baseHeaders,
      ...( ctx.accountId ? { 'x-auth-account-id': ctx.accountId } : {}),
      ...( ctx.userId ? { 'x-auth-user-id': ctx.userId } : {}),
      // NOT an authority tier — the audit trail. Its consumers are event metadata and the
      // ALS execution context; no authorization decision reads it. Dropping it here would
      // silently erase who was really acting from every event a hop emits.
      ...( ctx.impersonatedBy ? { 'x-auth-impersonated-by': ctx.impersonatedBy } : {})
    };
  };

  const post = ( path: string, { body, ...params }: Record<string, any> = {}, headers: Record<string, string | undefined> = {}, isMultipart: boolean = false ) => {
    const ctx = resolveContext();
    const enrichedParams = mergeContextMetadata( params, ctx );
    const requestPath = formatRequestParams( path, enrichedParams );
    const queryParams = parseQueryParams( path, enrichedParams );
    const { body: requestBody, headers: bodyHeaders } = buildRequestBody( body, isMultipart );

    const resolvedHeaders = resolveAuthHeaders({
      ...( defaultHeaders ?? {}),
      ...( bodyHeaders ?? {}),
      ...( headers ?? {})
    }, headers, ctx );
    
    return _fetch( `${config.baseUrl}${requestPath}${queryParams}`, { method: 'post',
      headers: filterUndefinedHeaders( resolvedHeaders ), 
      ...( requestBody !== undefined ? { body: requestBody } : {}),
      ...( config.includeCredentials
        ? { credentials: 'include' }
        : {})
    })
      .then( processFetchResponse )
      .then(( response: FetchResponse | void ) => { return ( response as any )?.bodyParsed; });
  };

  const get = ( path: string, params: Record<string, any> = {}, headers: Record<string, string | undefined> = {}) => {
    const ctx = resolveContext();
    const enrichedParams = mergeContextMetadata( params, ctx );
    const requestPath = formatRequestParams( path, enrichedParams );
    const queryParams = parseQueryParams( path, enrichedParams );

    const resolvedHeaders = resolveAuthHeaders({
      ...( defaultHeaders ?? {}),
      ...( headers ?? {})
    }, headers, ctx );
    
    // Even though most cdns will support larger urls (~8kb), we'll limit it to 1800 characters to avoid 414 errors with some proxies
    // Estimate size of the url
    let url = `${config.baseUrl}${requestPath}${queryParams}`;
    const urlSize = url.length;
    let method = 'get';
    if ( urlSize > 4800 ) {
      method = 'post';
      resolvedHeaders['X-HTTP-Method-Override'] = 'GET';
      resolvedHeaders['Content-Type'] = 'application/json';
      url = `${config.baseUrl}${requestPath}`;
    }
    return _fetch( url, { 
      method,
      headers: filterUndefinedHeaders( resolvedHeaders ),
      ...( config.includeCredentials ? { credentials: 'include' } : {}),
      ...( method === 'post' ? { body: JSON.stringify( enrichedParams ) } : {})
    })
      .then( processFetchResponse )
      .then(( response: FetchResponse | void ) => { return ( response as any )?.bodyParsed; });
  };

  const subscribe = ( path: string, params: Record<string, any> = {}, name: string, onChange: ( change: ReadmodelChange )=>void ) => {
    console.log( `Stablishing connection with subscription ${name}...` );
    const requestPath = formatRequestParams( path, params );
    const queryParams = parseQueryParams( path, params );
    const source = new EventSource( `${config.baseUrl}${requestPath}${queryParams}`, { withCredentials: config.includeCredentials });
    source.addEventListener( 'open', ( e ) => console.log( `Connections to the server established for subscription ${name}` ), false );
    source.onmessage = ( e ) => {
      if ( /Connected/.test( e.data )) return;
      onChange( JSON.parse( e.data ));
    };
    return { close: source.close.bind( source ) };
  };

  const setBearerToken = ( newToken: string ) => {
    defaultHeaders['Authorization'] = `Bearer ${newToken}`;
  };

  return {
    post,
    get,
    subscribe,
    setBearerToken
  };
};


export const getCustomExports = () => ({});